"use client";

import { useState, useEffect, useRef } from "react";
import { cn } from "@/app/lib/utils/cn";
import ThemeToggle from "@/app/ui/atomic/content/ThemeToggle";

interface Section {
  id: string;
  label: string;
}

interface SectionNavProps {
  sections: Section[];
}

export default function SectionNav({ sections }: SectionNavProps) {
  const [activeSection, setActiveSection] = useState<string>("");
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const visibleSectionsRef = useRef<Set<string>>(new Set());
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const isVisibleRef = useRef(false);

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId !== null) return;
      
      rafId = requestAnimationFrame(() => {
        const shouldBeVisible = window.scrollY > 400;
        if (shouldBeVisible !== isVisibleRef.current) {
          isVisibleRef.current = shouldBeVisible;
          setIsVisible(shouldBeVisible);
        }
        rafId = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    const sectionElements = sections
      .map(s => document.getElementById(s.id))
      .filter((el): el is HTMLElement => el !== null);

    if (sectionElements.length === 0) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          if (entry.isIntersecting) {
            visibleSectionsRef.current.add(id);
          } else {
            visibleSectionsRef.current.delete(id);
          }
        });

        const visibleIds = Array.from(visibleSectionsRef.current);
        if (visibleIds.length > 0) {
          const sectionOrder = sections.map(s => s.id);
          const topMostVisible = visibleIds.reduce((top, id) => {
            return sectionOrder.indexOf(id) < sectionOrder.indexOf(top) ? id : top;
          }, visibleIds[0]!);
          setActiveSection(topMostVisible);
        }
      },
      {
        rootMargin: "-20% 0px -60% 0px",
        threshold: 0
      }
    );

    sectionElements.forEach((el) => {
      observerRef.current?.observe(el);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [sections]);

  useEffect(() => {
    if (activeSection && mobileScrollRef.current) {
      const activeButton = mobileScrollRef.current.querySelector(`[data-section="${activeSection}"]`);
      if (activeButton) {
        activeButton.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }
    }
  }, [activeSection]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <>
      <nav 
        aria-label="Page sections (mobile)" 
        className={cn(
          "fixed left-0 right-0 top-0 z-50 block lg:hidden transition-all duration-300",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full pointer-events-none"
        )}
      >
        <div className="bg-(--bg-card)/95 backdrop-blur-sm border-b border-(--border-color) shadow-md">
          <div className="flex items-center px-4 py-2">
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={scrollToTop}
                className={cn(
                  "flex-shrink-0 min-w-[44px] h-[44px] flex items-center justify-center rounded-full transition-all duration-200",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-(--gold)",
                  "bg-(--purple) text-(--gold) hover:bg-(--gold) hover:text-(--purple)"
                )}
                aria-label="Back to top"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
              
              <ThemeToggle variant="mobile" />
            </div>
            
            <div 
              ref={mobileScrollRef}
              className="flex-1 flex items-center gap-2 overflow-x-auto scrollbar-hide ml-2"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  data-section={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={cn(
                    "flex-shrink-0 min-h-[44px] px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-(--gold)",
                    activeSection === section.id
                      ? "bg-(--gold) text-(--purple)"
                      : "bg-(--purple)/80 text-(--gold) hover:bg-(--purple)"
                  )}
                  aria-current={activeSection === section.id ? "true" : undefined}
                  tabIndex={0}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <nav 
        aria-label="Page sections" 
        className={cn(
          "fixed right-4 top-1/2 -translate-y-1/2 z-40 transition-all duration-300",
          "hidden lg:block",
          isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
        )}
      >
        <div className="bg-(--bg-card)/95 backdrop-blur-sm rounded-full py-4 px-3 shadow-lg border border-(--border-color)">
          <ul className="flex flex-col items-center gap-4">
            <li className="pb-4 mb-1 border-b-2 border-(--brand)/40">
              <button
                type="button"
                onClick={scrollToTop}
                style={{ width: '35px', height: '35px', minWidth: '35px', minHeight: '35px' }}
                className={cn(
                  "group relative flex items-center justify-center rounded-full transition-all duration-300",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-(--gold) focus-visible:ring-offset-2 focus-visible:ring-offset-(--bg-card)",
                  "bg-(--purple-accent)/60 hover:bg-(--gold) hover:scale-110"
                )}
                aria-label="Back to top"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
                </svg>
                <span className="absolute right-full mr-4 top-1/2 -translate-y-1/2 whitespace-nowrap bg-(--bg-card) text-(--text-primary) text-base px-4 py-2 rounded-lg shadow-md border border-(--border-color) opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Top of Page
                </span>
              </button>
            </li>
            {sections.map((section) => (
              <li key={section.id} className="flex justify-center">
                <button
                  type="button"
                  onClick={() => scrollToSection(section.id)}
                  style={{ width: '20px', height: '20px', minWidth: '20px', minHeight: '20px' }}
                  className={cn(
                    "group relative rounded-full transition-all duration-300",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-(--gold) focus-visible:ring-offset-2 focus-visible:ring-offset-(--bg-card)",
                    activeSection === section.id
                      ? "bg-(--gold)"
                      : "bg-(--purple-accent)/60 hover:bg-(--brand)"
                  )}
                  aria-label={`Jump to ${section.label}`}
                  aria-current={activeSection === section.id ? "true" : undefined}
                >
                  <span className="absolute right-full mr-4 top-1/2 -translate-y-1/2 whitespace-nowrap bg-(--bg-card) text-(--text-primary) text-base px-4 py-2 rounded-lg shadow-md border border-(--border-color) opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {section.label}
                  </span>
                </button>
              </li>
            ))}
            <li className="pt-4 mt-1 border-t-2 border-(--brand)/40">
              <ThemeToggle variant="sidebar" />
            </li>
          </ul>
        </div>
      </nav>
    </>
  );
}
